import { cn } from '@/lib/classNames';
import { Chevron, CloseThin } from '@/resources/assets/images';
import {
	useRef,
	createContext,
	type ComponentProps,
	type FunctionComponent,
	useContext,
	useMemo,
	KeyboardEvent,
	MouseEvent
} from 'react';
import { FilterModules, useFiltersRoot } from '../atoms/FiltersRoot';

type FiltersContextType = {
	isOpen: boolean;
	handleToggle: () => void;
	handleOpen: () => void;
	handleClose: () => void;
};

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

type FiltersWrapperProps = ComponentProps<'div'> & {
	moduleName: FilterModules;
};

const FilterWrapper = ({
	moduleName,
	className,
	...props
}: FiltersWrapperProps) => {
	const {
		openModule,
		handleClose,
		handleOpen: onOpen,
		handleToggle: onToggle
	} = useFiltersRoot();

	const wrapperRef = useRef<HTMLDivElement>(null);

	const isOpen = useMemo(
		() => openModule === moduleName,
		[moduleName, openModule]
	);

	const handleToggle = () => {
		onToggle(moduleName);
	};

	const handleOpen = () => {
		onOpen(moduleName);
	};

	return (
		<FiltersContext.Provider
			value={{
				isOpen,
				handleToggle,
				handleOpen,
				handleClose
			}}
		>
			<div
				{...props}
				data-is-opened={isOpen}
				ref={wrapperRef}
				className={cn(
					'relative h-full max-h-12 overflow-hidden border-b border-dm-black4 transition-[max-height] duration-200 ease-in-out',
					'data-[is-opened=true]:z-20 data-[is-opened=true]:max-h-full data-[is-opened=true]:overflow-visible data-[is-opened=true]:border-b data-[is-opened=true]:border-t data-[is-opened=true]:border-orange-brighter',
					className
				)}
			/>
		</FiltersContext.Provider>
	);
};

const useFilters = () => {
	const context = useContext(FiltersContext);

	if (!context) {
		throw new Error(
			'Filter must be used within a <Filters.Root /> and <Filters.Wrapper /> components'
		);
	}

	return context;
};

type FilterHeaderProps = {
	showClearFiltersButton?: boolean;
	activeFiltersCount?: number;
	onClearFilters?: VoidFunction;
	icon: FunctionComponent<ComponentProps<'svg'>>;
	title: string;
};

const FilterHeader = ({
	showClearFiltersButton = false,
	activeFiltersCount,
	icon: Icon,
	title,
	onClearFilters
}: FilterHeaderProps) => {
	const { isOpen, handleToggle } = useFilters();

	const handleUpdateState = (
		e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>
	) => {
		e.preventDefault();
		e.stopPropagation();

		handleToggle();
	};

	const handleClearFilters = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		onClearFilters?.();
	};

	return (
		<header
			onClick={handleUpdateState}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					handleUpdateState(e);
				}
			}}
			role="button"
			tabIndex={0}
			className="flex h-12 cursor-pointer select-none items-center justify-between gap-1 p-4 text-sm transition-colors hover:bg-dm-black3"
		>
			<p className="flex items-center gap-1.5 text-dm-white">
				<Icon
					data-is-filters-applied={
						activeFiltersCount && activeFiltersCount > 0
					}
					data-is-open={isOpen}
					className="aspect-square h-4 w-4 [&[data-is-filters-applied=true]_path]:fill-dm-gold [&[data-is-open=true]_path]:fill-dm-gold"
				/>

				{title}
			</p>

			<section className="flex items-center gap-1.5 [&_path]:fill-dm-white">
				{showClearFiltersButton && (
					<div className="flex h-5 items-center rounded border border-dm-black4 text-center">
						<p className="pointer-events-none w-4 text-xs font-bold">
							{activeFiltersCount}
						</p>

						<span className="inline-block h-full w-px border-l border-dm-gray align-middle" />

						<button
							className="flex aspect-square h-full w-5 cursor-pointer items-center justify-center"
							type="button"
							onClick={handleClearFilters}
						>
							<CloseThin className="h-2 w-2" />
						</button>
					</div>
				)}

				<Chevron
					data-is-open={isOpen}
					className={cn(
						'transition-transform duration-200',
						'data-[is-open=true]:rotate-180'
					)}
				/>
			</section>
		</header>
	);
};

const FilterContent = ({ className, ...props }: ComponentProps<'section'>) => {
	const { isOpen } = useFilters();

	return (
		<section
			data-is-open={isOpen}
			className={cn(
				'mx-4 flex origin-top scale-y-0 flex-col gap-3 border-t border-dm-black4 py-4 text-xs text-dm-white transition-transform',
				'data-[is-open=true]:scale-y-100',
				className
			)}
			{...props}
		/>
	);
};

export { useFilters };
export const FilterComponents = {
	Wrapper: FilterWrapper,
	Header: FilterHeader,
	Content: FilterContent
};
